import {
  baseApi,
  holdingsAccountTag,
  holdingTradeRipple,
  holdingTradesAccountTag,
} from "../api";
import {
  contextParams,
  SectionEnvelopeWire,
  SectionRequestArgs,
  SectionResponse,
  toSectionResponse,
  withRejectionStatus,
} from "../consistency";
import {
  HoldingsSummaryWire,
  HoldingWire,
  toHolding,
  toHoldingsSummary,
} from "./canonical";
import {
  AccountCashPayload,
  ActivityPage,
  BuyHoldingPayload,
  DividendPayload,
  EditTradeCommandPayload,
  GetAccountActivityArgs,
  Holding,
  HoldingFeePayload,
  HoldingsSummary,
  DirectedAccountCashPayload,
  LedgerCashWarning,
  SellHoldingPayload,
  TradeCommand,
  TradeCorrectionResult,
  TradeWriteResult,
  WithholdingTaxPayload,
} from "./types";

interface GetHoldingsArgs extends SectionRequestArgs {
  /** Required by the server — a request without it is a 400. */
  accountId: number;
  includeClosed?: boolean;
}

interface GetHoldingsSummaryArgs {
  accountId: number;
  /** Ripple-only: an activation or edit invalidates `Investment:{id}`, and the summary has to re-establish the context after it. */
  investmentId: number;
}

/** Ripple-only: a linked broker's write, or a cashback buy's owned income, also moves transactions. */
interface LedgerRippleArgs {
  movesTransactions: boolean;
}

interface BuyHoldingArgs extends LedgerRippleArgs {
  /** Ripple-only (not a body key) — `payload.accountId` is the account id, a different number. */
  investmentId: number;
  payload: BuyHoldingPayload;
}

interface SellHoldingArgs extends LedgerRippleArgs {
  /** Same role as `BuyHoldingArgs.investmentId` — ripple only, not a body key. */
  investmentId: number;
  payload: SellHoldingPayload;
}

/** The standalone fee — cash out against a position, and no charge of its own. */
interface HoldingFeeArgs extends LedgerRippleArgs {
  investmentId: number;
  payload: HoldingFeePayload;
}

/** Its own args because the payload may carry `taxWithheld` — sharing `HoldingFeeArgs` would re-open that key on `/holdings/fee`. */
interface DividendArgs extends LedgerRippleArgs {
  investmentId: number;
  payload: DividendPayload;
}

/** Withholding tax has its own DTO server-side, so it gets its own args here. */
interface WithholdingTaxArgs extends LedgerRippleArgs {
  investmentId: number;
  payload: WithholdingTaxPayload;
}

/** Account fee — no `holdingId` anywhere in the payload, by construction. */
interface AccountCashArgs extends LedgerRippleArgs {
  investmentId: number;
  payload: AccountCashPayload;
}

interface DirectedAccountCashArgs extends LedgerRippleArgs {
  investmentId: number;
  payload: DirectedAccountCashPayload;
}

interface ActivityPageResponse extends SectionEnvelopeWire<
  Omit<ActivityPage, "pagination">
> {
  pagination?: ActivityPage["pagination"];
}

interface EditTradeCommandArgs extends LedgerRippleArgs {
  commandId: string;
  /** Both ripple-only: the server reads the account and position from the stored command, not the request. */
  investmentId: number;
  accountId: number;
  payload: EditTradeCommandPayload;
}

/** The PATCH envelope as it arrives: D8 warnings sit beside `result`, so this endpoint can't use the base query's unwrap. */
interface EditTradeCommandResponse {
  result: TradeCommand;
  warnings?: LedgerCashWarning[];
}

interface DeleteTradeCommandArgs extends LedgerRippleArgs {
  commandId: string;
  /** Ripple-only, same as `EditTradeCommandArgs` — a DELETE carries no body. */
  investmentId: number;
  accountId: number;
}

interface PayloadRippleArgs extends LedgerRippleArgs {
  investmentId: number;
  payload: { accountId: number };
}

const payloadTradeRipple = (
  _result: unknown,
  _error: unknown,
  { investmentId, payload, movesTransactions }: PayloadRippleArgs,
) =>
  holdingTradeRipple({
    investmentId,
    accountId: payload.accountId,
    movesTransactions,
  });

/**
 * `LIST` sentinel plus a per-account tag plus one tag per row, mirroring `accountsListTags`.
 * The per-account tag is string-namespaced so it can't collide with a row id, and is what a trade invalidates.
 */
const holdingsListTags = (result: Holding[] | undefined, accountId: number) => [
  { type: "Holding" as const, id: "LIST" },
  holdingsAccountTag(accountId),
  ...(result?.map((holding) => ({
    type: "Holding" as const,
    id: holding.id,
  })) ?? []),
];

/**
 * Every write opts out of the envelope unwrap (`unwrapResult: false`): the D8 cash warnings sit beside `result`,
 * so the default unwrap would resolve to the bare string and silently drop them.
 */
export const holdingsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // A versioned entry provides no tags: a write refetches the summary alone, and its new pair re-keys this entry.
    getHoldings: builder.query<SectionResponse<Holding[]>, GetHoldingsArgs>({
      query: ({ accountId, includeClosed, context }) => ({
        url: "/holdings",
        params: {
          accountId,
          ...(includeClosed === undefined ? {} : { includeClosed }),
          ...contextParams(context),
        },
        unwrapResult: false,
      }),
      transformResponse: (
        response: SectionEnvelopeWire<HoldingWire[] | undefined>,
      ) => toSectionResponse(response, (rows) => (rows ?? []).map(toHolding)),
      transformErrorResponse: withRejectionStatus,
      providesTags: (result, _error, { accountId, context }) =>
        context ? [] : holdingsListTags(result?.result, accountId),
    }),

    // Never versioned: it establishes the context the other three sections then supply.
    getHoldingsSummary: builder.query<
      SectionResponse<HoldingsSummary>,
      GetHoldingsSummaryArgs
    >({
      query: ({ accountId }) => ({
        url: "/holdings/summary",
        params: { accountId },
        unwrapResult: false,
      }),
      transformResponse: (response: SectionEnvelopeWire<HoldingsSummaryWire>) =>
        toSectionResponse(response, toHoldingsSummary),
      transformErrorResponse: withRejectionStatus,
      providesTags: (_result, _error, { accountId, investmentId }) => [
        holdingsAccountTag(accountId),
        holdingTradesAccountTag(accountId),
        { type: "Investment", id: investmentId },
      ],
    }),

    buyHolding: builder.mutation<TradeWriteResult, BuyHoldingArgs>({
      query: ({ payload }) => ({
        url: "/holdings/buy",
        method: "post",
        unwrapResult: false,
        data: payload,
      }),
      invalidatesTags: payloadTradeRipple,
    }),

    sellHolding: builder.mutation<TradeWriteResult, SellHoldingArgs>({
      query: ({ payload }) => ({
        url: "/holdings/sell",
        method: "post",
        unwrapResult: false,
        data: payload,
      }),
      invalidatesTags: payloadTradeRipple,
    }),

    recordHoldingDividend: builder.mutation<TradeWriteResult, DividendArgs>({
      query: ({ payload }) => ({
        url: "/holdings/dividend",
        method: "post",
        unwrapResult: false,
        data: payload,
      }),
      invalidatesTags: payloadTradeRipple,
    }),

    recordHoldingFee: builder.mutation<TradeWriteResult, HoldingFeeArgs>({
      query: ({ payload }) => ({
        url: "/holdings/fee",
        method: "post",
        unwrapResult: false,
        data: payload,
      }),
      invalidatesTags: payloadTradeRipple,
    }),

    // Tax withheld at source on a position's income; same ripple as the trades above.
    recordWithholdingTax: builder.mutation<
      TradeWriteResult,
      WithholdingTaxArgs
    >({
      query: ({ payload }) => ({
        url: "/holdings/tax",
        method: "post",
        unwrapResult: false,
        data: payload,
      }),
      invalidatesTags: payloadTradeRipple,
    }),

    // Touches no position but moves the account's cash and return, so it uses the same ripple, not a narrower one.
    recordAccountFee: builder.mutation<TradeWriteResult, AccountCashArgs>({
      query: ({ payload }) => ({
        url: "/holdings/account-fee",
        method: "post",
        unwrapResult: false,
        data: payload,
      }),
      invalidatesTags: payloadTradeRipple,
    }),

    recordInterest: builder.mutation<TradeWriteResult, DirectedAccountCashArgs>(
      {
        query: ({ payload }) => ({
          url: "/holdings/interest",
          method: "post",
          unwrapResult: false,
          data: payload,
        }),
        invalidatesTags: payloadTradeRipple,
      },
    ),

    recordAdjustment: builder.mutation<
      TradeWriteResult,
      DirectedAccountCashArgs
    >({
      query: ({ payload }) => ({
        url: "/holdings/adjustment",
        method: "post",
        unwrapResult: false,
        data: payload,
      }),
      invalidatesTags: payloadTradeRipple,
    }),

    /**
     * Reuses the command-only tag so `holdingTradeRipple` refreshes it too; a corporate action landing from the
     * catalogue (not a ledger write) only appears on the next ledger write or reload.
     */
    getAccountActivity: builder.query<
      SectionResponse<ActivityPage>,
      GetAccountActivityArgs
    >({
      query: ({ accountId, context, generation, ...filters }) => ({
        url: "/holdings/activity",
        params: {
          accountId,
          ...Object.fromEntries(
            Object.entries(filters).filter(([, value]) => value !== undefined),
          ),
          ...contextParams(context),
        },
        unwrapResult: false,
      }),
      // Page mode reports its totals beside `result`, not inside it.
      transformResponse: ({ pagination, ...envelope }: ActivityPageResponse) =>
        toSectionResponse(envelope, (result) => ({
          ...result,
          ...(pagination === undefined ? {} : { pagination }),
        })),
      transformErrorResponse: withRejectionStatus,
      providesTags: (_result, _error, { accountId, context }) =>
        context ? [] : [holdingTradesAccountTag(accountId)],
    }),

    // A correction replaces every row the original submission wrote, so it uses the same ripple as a trade.
    editTradeCommand: builder.mutation<
      TradeCorrectionResult,
      EditTradeCommandArgs
    >({
      query: ({ commandId, payload }) => ({
        url: "/holdings/trades/" + commandId,
        method: "patch",
        unwrapResult: false,
        data: payload,
      }),
      // The corrected command keeps its own name here rather than the envelope's
      // `result`, so callers read `command` and `warnings` as two peers.
      transformResponse: (response: EditTradeCommandResponse) => ({
        command: response.result,
        warnings: response.warnings,
      }),
      invalidatesTags: (
        _result,
        _error,
        { investmentId, accountId, movesTransactions },
      ) => holdingTradeRipple({ investmentId, accountId, movesTransactions }),
    }),

    // Answers `200 { warnings }`, not `204`: a delete can take the account negative, and a 204 has nowhere to say so.
    deleteTradeCommand: builder.mutation<
      TradeWriteResult,
      DeleteTradeCommandArgs
    >({
      query: ({ commandId }) => ({
        url: "/holdings/trades/" + commandId,
        method: "delete",
        unwrapResult: false,
      }),
      invalidatesTags: (
        _result,
        _error,
        { investmentId, accountId, movesTransactions },
      ) => holdingTradeRipple({ investmentId, accountId, movesTransactions }),
    }),
  }),
});

export const {
  useGetHoldingsQuery,
  useGetHoldingsSummaryQuery,
  useGetAccountActivityQuery,
  useLazyGetAccountActivityQuery,
  useEditTradeCommandMutation,
  useDeleteTradeCommandMutation,
  useBuyHoldingMutation,
  useSellHoldingMutation,
  useRecordHoldingDividendMutation,
  useRecordHoldingFeeMutation,
  useRecordWithholdingTaxMutation,
  useRecordAccountFeeMutation,
  useRecordInterestMutation,
  useRecordAdjustmentMutation,
} = holdingsApi;
